import { FlyoverPref, Layers, RotationMode, ZIndexes } from "../constants";
import { CircleHitbox, GroupHitbox, type Hitbox, RectangleHitbox } from "../utils/hitbox";
import { DefinitionType } from "../utils/objectDefinitions";
import { Vec } from "../utils/vector";
import { type RawObstacleDefinition } from "./obstacles";

/*
    The pieces of Fargo's buildings of 1.07 (the owner's picture, 29 October 2026): the police station's and the cars of
    its lots, the prison's lighthouse's, the mansions', Pool Day's (1.07.1: the pool villa's until then). Each picture is its id (or its id and variation), drawn by the building's generator in tools/fargo/
    (client/public/img/game/obstacles/police/ and the others, 20 px a unit), its back to the north (rotation 0: its front
    at the bottom of the picture); a car's front is its east end. What a piece holds is its loot table's
    (server/src/data/lootTables.ts).
*/

/** a piece of one of Fargo's 1.07 buildings: its picture is its id */
const piece = (
    idString: string,
    name: string,
    hitbox: Hitbox,
    options: Partial<RawObstacleDefinition> & Pick<RawObstacleDefinition, "material" | "health">
): RawObstacleDefinition => ({
    idString,
    name,
    defType: DefinitionType.Obstacle,
    hideOnMap: true,
    rotationMode: RotationMode.Limited,
    scale: { spawnMin: 1, spawnMax: 1, destroy: 0.9 },
    hitbox,
    ...options
} as RawObstacleDefinition);

/** a piece that doesn't break */
const fixture = (idString: string, name: string, hitbox: Hitbox, options: Partial<RawObstacleDefinition> = {}): RawObstacleDefinition => piece(idString, name, hitbox, {
    material: "metal_heavy",
    health: 1000,
    indestructible: true,
    noResidue: true,
    frames: { particle: "metal_particle" },
    ...options
});

/** a car: steel, hard cover that turns bullets, shown on the map as the town's cars are */
const car = (idString: string, name: string, length: number, width: number, variations?: 2 | 3 | 4): RawObstacleDefinition => ({
    ...fixture(idString, name, RectangleHitbox.fromRect(length, width), { reflectBullets: true, allowFlyover: FlyoverPref.Never }),
    hideOnMap: false,
    ...(variations ? { variations } : {})
});

/**
 * a thing on a wall (a screen, a board, a chart): drawn over the floor along the wall's face, nothing stops on it
 */
const onWall = (idString: string, name: string, length: number, depth = 0.8): RawObstacleDefinition => fixture(idString, name, RectangleHitbox.fromRect(length, depth), {
    material: "wood",
    noCollisions: true,
    noBulletCollision: true,
    noMeleeCollision: true,
    noHitEffect: true,
    allowFlyover: FlyoverPref.Always,
    zIndex: ZIndexes.ObstaclesLayer1,
    frames: { particle: "furniture_particle" }
});

/** an invisible piece (what it is is drawn in its building's own pictures) */
const hidden = (idString: string, name: string, hitbox: Hitbox, options: Partial<RawObstacleDefinition> & Pick<RawObstacleDefinition, "material">): RawObstacleDefinition =>
    fixture(idString, name, hitbox, { invisible: true, frames: { particle: "mansion_stone_particle" }, ...options });

/** a piece of furniture that breaks (its picture and, unless it has none, its residue: its id and _residue) */
const furniture = (
    idString: string,
    name: string,
    hitbox: Hitbox,
    material: RawObstacleDefinition["material"],
    health: number,
    particle: string,
    options: Partial<RawObstacleDefinition> & { residue?: string | false } = {}
): RawObstacleDefinition => {
    const { residue, ...rest } = options;
    return piece(idString, name, hitbox, {
        material,
        health,
        allowFlyover: FlyoverPref.Always,
        ...(residue === false ? { noResidue: true } : {}),
        frames: { particle, ...(residue === false ? {} : { residue: residue ?? `${idString}_residue` }) },
        ...rest
    });
};

/** how long each run of bars is either side of a cell's door at the police station (its cells: tools/fargo/police.py) */
export const POLICE_CELL_BARS = 5.23;

/**
 * the street lights along the town maps' roads (0.98.1, the owner, 30 October 2026: "add some street lights next to
 * roads, they're shootable and destroyeable tho"): `reach`, from the post to its lamp out over the road (its picture's,
 * tools/fargo/street_lights.py REACH); `light`, how far round the lamp its light falls at night (the page's night,
 * managers/nightLight.ts); `out`, from a road's middle line to the post (the asphalt is 52 across: just off it, the
 * lamp over its edge); `spacing`, along a road between one and the next, every other one across the road
 * (server/src/data/maps.ts, `townStreetLights`)
 */
export const STREET_LIGHT = Object.freeze({ reach: 7.4, light: 17, out: 28.5, spacing: 64 });

export const FARGO_OBSTACLES: readonly RawObstacleDefinition[] = [
    // ---------------- the police station (tools/fargo/police.py) ----------------
    // the cars: the patrol cars (a sedan and an SUV), the prisoner van in the sally port, the visitors' cars
    car("police_car", "Police Car", 31, 13.6, 2),
    car("police_van", "Prisoner Van", 38, 16),
    car("parked_car", "Car", 30, 13.4, 3),
    // ---------------- the streets' cars (0.98.4, tools/storm/cars.py) ----------------
    // (the owner, 31 October 2026: "add a few different cars in road downtown ,riversiden, the camp, some SUV's, some
    // toyotas, some normal cars, that also get picked up by the water"): family saloons, SUVs and small hatchbacks parked
    // along downtown's, Riverside's and the camp's roads (server/src/data/maps.ts `townStreetCars`); in a thunderstorm
    // the flood carries them off (server/src/storm/stormManager.ts)
    car("street_sedan", "Car", 30, 13.4, 3),
    car("street_suv", "SUV", 32, 14.4, 3),
    car("street_hatch", "Hatchback", 24, 12.6, 3),
    // the bullpen's desks, each its computer, phone and files; the front desk behind the lobby's glass; booking's
    piece("police_desk", "Desk", RectangleHitbox.fromRect(12, 6), {
        material: "wood", health: 120, hasLoot: true, lootTable: "desk", variations: 2, allowFlyover: FlyoverPref.Always,
        frames: { particle: "police_desk_particle", residue: "police_desk_residue" }
    }),
    piece("police_front_desk", "Front Desk", RectangleHitbox.fromRect(16, 5), {
        material: "wood", health: 160, hasLoot: true, lootTable: "desk", allowFlyover: FlyoverPref.Always,
        frames: { particle: "police_desk_particle", residue: "police_front_desk_residue" }
    }),
    piece("police_booking_counter", "Booking Counter", RectangleHitbox.fromRect(16, 5), {
        material: "wood", health: 160, hasLoot: true, lootTable: "small_desk", allowFlyover: FlyoverPref.Always,
        frames: { particle: "police_desk_particle", residue: "police_front_desk_residue" }
    }),
    piece("police_monitor_desk", "Recording Desk", RectangleHitbox.fromRect(12, 4.4), {
        material: "wood", health: 120, hasLoot: true, lootTable: "desk", allowFlyover: FlyoverPref.Always,
        frames: { particle: "police_desk_particle", residue: "police_desk_residue" }
    }),
    // the briefing room's rows of seats, the speaker's lectern, the screen on the wall
    piece("police_seats", "Seats", RectangleHitbox.fromRect(22, 4.6), {
        material: "metal_light", health: 110, allowFlyover: FlyoverPref.Always,
        frames: { particle: "police_seat_particle", residue: "police_seats_residue" }
    }),
    piece("police_lectern", "Lectern", RectangleHitbox.fromRect(4.4, 3.4), {
        material: "wood", health: 60, allowFlyover: FlyoverPref.Always,
        frames: { particle: "police_desk_particle", residue: "police_lectern_residue" }
    }),
    onWall("police_screen", "Screen", 18),
    // the detectives' case table: photographs and string over a map
    piece("police_case_table", "Case Table", RectangleHitbox.fromRect(12, 6.4), {
        material: "wood", health: 1000, indestructible: true, noResidue: true, allowFlyover: FlyoverPref.Always,
        frames: { particle: "police_desk_particle" }
    }),
    // the break room's coffee, the interview rooms' table, the copier, the fingerprint scanner
    piece("police_coffee_counter", "Coffee Counter", RectangleHitbox.fromRect(13, 4.6), {
        material: "wood", health: 110, hasLoot: true, lootTable: "small_table", allowFlyover: FlyoverPref.Always,
        frames: { particle: "police_desk_particle", residue: "police_coffee_counter_residue" }
    }),
    piece("police_interview_table", "Interview Table", RectangleHitbox.fromRect(9, 5.6), {
        material: "metal_light", health: 1000, indestructible: true, noResidue: true, allowFlyover: FlyoverPref.Always,
        frames: { particle: "metal_particle" }
    }),
    piece("police_copier", "Copier", RectangleHitbox.fromRect(6.6, 5.6), {
        material: "appliance", health: 110, frames: { particle: "police_copier_particle", residue: "police_copier_residue" }
    }),
    piece("police_fingerprint", "Fingerprint Scanner", RectangleHitbox.fromRect(4, 3.4), {
        material: "appliance", health: 60, allowFlyover: FlyoverPref.Always,
        frames: { particle: "police_copier_particle", residue: "police_fingerprint_residue" }
    }),
    // the evidence room's shelves (what's kept there)
    piece("police_evidence_shelf", "Evidence Shelf", RectangleHitbox.fromRect(13, 4.4), {
        material: "iron", health: 160, hasLoot: true, lootTable: "police_evidence", reflectBullets: true,
        frames: { particle: "police_shelf_particle", residue: "police_evidence_shelf_residue" }
    }),
    // the lobby's metal detector: its two posts, the way through between them
    piece("police_metal_detector", "Metal Detector", new GroupHitbox(
        RectangleHitbox.fromRect(1.2, 2.6, Vec(-4.6, 0)),
        RectangleHitbox.fromRect(1.2, 2.6, Vec(4.6, 0))
    ), {
        material: "metal_light", health: 90, allowFlyover: FlyoverPref.Always,
        frames: { particle: "police_copier_particle", residue: "police_metal_detector_residue" }
    }),
    // outside: the flag, the station's sign, the barriers, the cones, the motor pool's fuel pump, bollards
    fixture("police_flagpole", "Flagpole", new CircleHitbox(0.7), { allowFlyover: FlyoverPref.Never, zIndex: ZIndexes.ObstaclesLayer3 }),
    fixture("police_sign", "Police Station Sign", RectangleHitbox.fromRect(22, 4), {
        // (the rocks' two, rock_particle_1 and _2: there's no plain rock_particle, 0.98.1)
        material: "stone", allowFlyover: FlyoverPref.Never, frames: { particle: "rock_particle" }, particleVariations: 2
    }),
    piece("police_barrier", "Police Barrier", RectangleHitbox.fromRect(10, 1.6), {
        material: "wood", health: 60, allowFlyover: FlyoverPref.Always,
        frames: { particle: "police_barrier_particle", residue: "police_barrier_residue" }
    }),
    piece("traffic_cone", "Traffic Cone", new CircleHitbox(1.1), {
        material: "cardboard", health: 25, allowFlyover: FlyoverPref.Always, noResidue: true,
        frames: { particle: "traffic_cone_particle" }
    }),
    fixture("police_fuel_pump", "Fuel Pump", RectangleHitbox.fromRect(5, 3.4), { reflectBullets: true }),
    fixture("street_bollard", "Bollard", new CircleHitbox(0.75), { allowFlyover: FlyoverPref.Never, frames: { particle: "bollard_particle" } }),
    // the motor pool's gate: its barrier arm, raised (drawn over everyone, stopping nobody)
    fixture("police_gate_arm", "Barrier Arm", RectangleHitbox.fromRect(16, 0.8), {
        material: "metal_light", noCollisions: true, noBulletCollision: true, noMeleeCollision: true, noHitEffect: true,
        allowFlyover: FlyoverPref.Always, zIndex: ZIndexes.ObstaclesLayer3
    }),

    // ---------------- the prison's lighthouse (tools/fargo/lighthouse.py) ----------------
    // the tower's spiral stair (up to the lantern, closed off), the watch post's desk on the prison, its searchlight
    fixture("lighthouse_stair", "Spiral Stair", new CircleHitbox(5.6), { allowFlyover: FlyoverPref.Never }),
    piece("watch_desk", "Watch Desk", RectangleHitbox.fromRect(12, 4.6), {
        material: "iron", health: 150, hasLoot: true, lootTable: "desk", reflectBullets: true, allowFlyover: FlyoverPref.Always,
        frames: { particle: "watch_desk_particle", residue: "watch_desk_residue" }
    }),
    fixture("searchlight", "Searchlight", new CircleHitbox(3.2), { reflectBullets: true, allowFlyover: FlyoverPref.Never }),

    // ---------------- Pool Day, the public pool (tools/fargo/pool.py) ----------------
    // the halls' lockers (blue in the men's, red in the women's), their benches, the cubicles' doors, the urinals, the
    // basins under the mirrors and the red hall's vanity island
    furniture("pool_lockers_blue", "Lockers", RectangleHitbox.fromRect(12, 3.4), "metal_light", 120, "pool_blue_particle", { residue: "pool_lockers_residue", hasLoot: true, lootTable: "pool_locker" }),
    furniture("pool_lockers_red", "Lockers", RectangleHitbox.fromRect(12, 3.4), "metal_light", 120, "pool_red_particle", { residue: "pool_lockers_residue", hasLoot: true, lootTable: "pool_locker" }),
    furniture("pool_bench", "Bench", RectangleHitbox.fromRect(16, 3.2), "wood", 60, "pool_bench_particle"),
    {
        ...piece("pool_stall_door_blue", "Cubicle Door", RectangleHitbox.fromRect(7.6, 1), {
            material: "wood", health: 60, noResidue: true, zIndex: ZIndexes.ObstaclesLayer3, frames: { particle: "pool_blue_particle" }
        }),
        isDoor: true,
        hingeOffset: Vec(-3.8, 0)
    } as RawObstacleDefinition,
    {
        ...piece("pool_stall_door_red", "Cubicle Door", RectangleHitbox.fromRect(7.6, 1), {
            material: "wood", health: 60, noResidue: true, zIndex: ZIndexes.ObstaclesLayer3, frames: { particle: "pool_red_particle" }
        }),
        isDoor: true,
        hingeOffset: Vec(-3.8, 0)
    } as RawObstacleDefinition,
    furniture("pool_urinal", "Urinal", RectangleHitbox.fromRect(2.6, 1.8), "porcelain", 80, "pool_white_particle", { residue: false }),
    furniture("pool_vanity", "Basins", RectangleHitbox.fromRect(16, 3.6), "porcelain", 120, "pool_white_particle"),
    furniture("pool_vanity_island", "Vanity", RectangleHitbox.fromRect(30, 6), "stone", 200, "pool_red_particle"),
    // the deck: the sunbeds, the springboard, the ladders, the lifeguard's chair, the clock over the sunbeds, the deck's
    // showers; the inflatables (they pop when shot: nothing stops on them)
    piece("pool_lounger", "Sun Lounger", RectangleHitbox.fromRect(4, 12), {
        material: "wood", health: 70, allowFlyover: FlyoverPref.Always,
        frames: { particle: "pool_lounger_particle", residue: "pool_lounger_residue" }
    }),
    fixture("pool_diving_board", "Springboard", RectangleHitbox.fromRect(10, 2.4), {
        material: "metal_light", noCollisions: true, allowFlyover: FlyoverPref.Always, zIndex: ZIndexes.ObstaclesLayer2
    }),
    fixture("pool_ladder", "Pool Ladder", RectangleHitbox.fromRect(4, 2.6), {
        material: "metal_light", noCollisions: true, noBulletCollision: true, allowFlyover: FlyoverPref.Always, zIndex: ZIndexes.ObstaclesLayer1
    }),
    fixture("lifeguard_chair", "Lifeguard Chair", RectangleHitbox.fromRect(6, 7), {
        material: "metal_light", noBulletCollision: true, allowFlyover: FlyoverPref.Never, zIndex: ZIndexes.ObstaclesLayer4
    }),
    // (the pole is what stands in the way; the clock is drawn over everyone's heads)
    fixture("pool_clock", "Clock", new CircleHitbox(0.6), { material: "metal_light", allowFlyover: FlyoverPref.Never, zIndex: ZIndexes.ObstaclesLayer5 }),
    fixture("pool_shower_post", "Shower", new CircleHitbox(0.7), { material: "metal_light", allowFlyover: FlyoverPref.Never }),
    piece("pool_float", "Flamingo", RectangleHitbox.fromRect(6, 4.6), {
        material: "cardboard", health: 20, noCollisions: true, noResidue: true, allowFlyover: FlyoverPref.Always,
        frames: { particle: "pool_float_particle" }
    }),
    piece("beach_ball", "Beach Ball", new CircleHitbox(1.3), {
        material: "cardboard", health: 10, noCollisions: true, allowFlyover: FlyoverPref.Always, rotationMode: RotationMode.Full,
        frames: { particle: "beach_ball_particle", residue: "beach_ball_residue" }
    }),
    piece("swim_ring", "Swim Ring", new CircleHitbox(2.2), {
        material: "cardboard", health: 15, noCollisions: true, allowFlyover: FlyoverPref.Always, rotationMode: RotationMode.Full,
        frames: { particle: "swim_ring_particle", residue: "swim_ring_residue" }
    }),
    piece("wet_floor_sign", "Wet Floor Sign", RectangleHitbox.fromRect(2.4, 1.8), {
        material: "cardboard", health: 15, noCollisions: true, noResidue: true, allowFlyover: FlyoverPref.Always,
        frames: { particle: "beach_ball_particle" }
    }),
    // the lobby: the ticket desk, the lifeguard's first aid cabinet and stretcher, the kiosk's counter, its freezer and
    // its menu board
    furniture("pool_ticket_desk", "Ticket Desk", RectangleHitbox.fromRect(14, 4.4), "wood", 140, "pool_white_particle", { hasLoot: true, lootTable: "small_drawer" }),
    furniture("first_aid_cabinet", "First Aid Cabinet", RectangleHitbox.fromRect(6, 1.6), "metal_light", 80, "pool_white_particle", { hasLoot: true, lootTable: "pool_first_aid" }),
    furniture("pool_stretcher", "Stretcher", RectangleHitbox.fromRect(5, 13), "wood", 60, "pool_kiosk_particle"),
    furniture("kiosk_counter", "Kiosk Counter", RectangleHitbox.fromRect(14, 4), "wood", 160, "pool_kiosk_particle", { hasLoot: true, lootTable: "pool_kiosk" }),
    furniture("ice_cream_freezer", "Ice Cream Freezer", RectangleHitbox.fromRect(8, 4.4), "appliance", 140, "pool_white_particle", { hasLoot: true, lootTable: "pool_kiosk" }),
    onWall("kiosk_menu", "Menu Board", 12),
    // an iron gate (the mansions'): a door of bars, bullets through it
    {
        ...piece("iron_gate", "Iron Gate", RectangleHitbox.fromRect(10.2, 1.4), {
            material: "metal_heavy", health: 1000, indestructible: true, noResidue: true, noBulletCollision: true,
            allowFlyover: FlyoverPref.Always, zIndex: ZIndexes.ObstaclesLayer3, frames: { particle: "metal_particle" }
        }),
        isDoor: true,
        hingeOffset: Vec(-5.1, 0),
        animationDuration: 350,
        doorSound: "metal_door"
    } as RawObstacleDefinition,

    // ---------------- the mansions (tools/fargo/mansions.py, tools/fargo/furniture.py) ----------------
    // the grounds: the fence's stone piers and iron railings (bullets through the railings, nobody over them), the
    // drive's gate's piers with their lanterns, the fountain, the urns, the air conditioners, the garage's door, the cars
    fixture("mansion_pier", "Pier", RectangleHitbox.fromRect(2.4, 2.4), {
        material: "stone", allowFlyover: FlyoverPref.Never, zIndex: ZIndexes.ObstaclesLayer2, frames: { particle: "mansion_stone_particle" }
    }),
    fixture("mansion_gate_pier", "Gate Pier", RectangleHitbox.fromRect(3.4, 3.4), {
        material: "stone", allowFlyover: FlyoverPref.Never, zIndex: ZIndexes.ObstaclesLayer2, frames: { particle: "mansion_stone_particle" }
    }),
    fixture("mansion_railing", "Railing", RectangleHitbox.fromRect(14.4, 1), {
        noBulletCollision: true, allowFlyover: FlyoverPref.Always, zIndex: ZIndexes.ObstaclesLayer1
    }),
    fixture("mansion_fountain", "Fountain", new CircleHitbox(9), {
        material: "stone", allowFlyover: FlyoverPref.Always, rotationMode: RotationMode.Full, frames: { particle: "mansion_stone_particle" }
    }),
    furniture("garden_urn", "Urn", new CircleHitbox(1.8), "stone", 100, "mansion_stone_particle", { rotationMode: RotationMode.Full }),
    furniture("ac_unit", "Air Conditioner", RectangleHitbox.fromRect(5, 4), "appliance", 120, "metal_particle", { reflectBullets: true }),
    furniture("mansion_garage_door", "Garage Door", RectangleHitbox.fromRect(21.76, 1.51), "metal_light", 300, "mansion_white_particle", {
        reflectBullets: true, allowFlyover: FlyoverPref.Never
    }),
    car("mansion_car", "Car", 30, 13.4, 3),
    // the stairs: a flight up towards the east at rotation 0 (each house's flights stand turned up towards the north),
    // the closet under the gallery past its top, solid downstairs only, so that the flight's top lets out upstairs only;
    // its side open to the hall railed downstairs and on the flight (A's balusters, B's glass); the gallery's railing
    // over the hall, upstairs only, nothing thrown over it
    hidden("mansion_flight", "Stairs", RectangleHitbox.fromRect(30, 10), {
        material: "wood", isStair: true, activeEdges: { high: 1, low: 3 }, noLanding: true, zIndex: ZIndexes.BuildingsFloor,
        frames: { particle: "mansion_wood_particle" }
    }),
    hidden("mansion_stair_store", "Closet", RectangleHitbox.fromRect(10.6, 16), {
        material: "wood", collideWithLayers: Layers.Equal, allowFlyover: FlyoverPref.Never, frames: { particle: "mansion_wood_particle" }
    }),
    fixture("mansion_stair_rail", "Balustrade", RectangleHitbox.fromRect(0.8, 30), {
        material: "wood", collideWithLayers: Layers.Adjacent, noBulletCollision: true, allowFlyover: FlyoverPref.Always,
        zIndex: ZIndexes.ObstaclesLayer1, frames: { particle: "mansion_wood_particle" }
    }),
    fixture("mansion_glass_rail", "Glass Balustrade", RectangleHitbox.fromRect(0.8, 30), {
        material: "glass", collideWithLayers: Layers.Adjacent, noBulletCollision: true, allowFlyover: FlyoverPref.Always,
        zIndex: ZIndexes.ObstaclesLayer1, frames: { particle: "window_particle" }
    }),
    fixture("mansion_gallery_rail_a", "Balustrade", RectangleHitbox.fromRect(18, 0.8), {
        material: "stone", collideWithLayers: Layers.Equal, noBulletCollision: true, allowFlyover: FlyoverPref.Never,
        zIndex: ZIndexes.ObstaclesLayer1, frames: { particle: "mansion_stone_particle" }
    }),
    fixture("mansion_gallery_rail_b", "Glass Balustrade", RectangleHitbox.fromRect(22, 0.8), {
        material: "glass", collideWithLayers: Layers.Equal, noBulletCollision: true, allowFlyover: FlyoverPref.Never,
        zIndex: ZIndexes.ObstaclesLayer1, frames: { particle: "window_particle" }
    }),
    // ---- A, the old house: the hall, the library, the drawing room, the dining room, the kitchen; upstairs the
    // master's rooms, the study, the guest room
    furniture("hall_table", "Hall Table", new CircleHitbox(4.2), "wood", 120, "mansion_wood_particle", { rotationMode: RotationMode.Full }),
    furniture("hall_console", "Console Table", RectangleHitbox.fromRect(12, 4), "wood", 100, "mansion_wood_particle", { hasLoot: true, lootTable: "small_drawer" }),
    furniture("chesterfield", "Chesterfield", RectangleHitbox.fromRect(16, 6.4), "wood", 110, "mansion_leather_particle"),
    furniture("wingback", "Wingback Chair", RectangleHitbox.fromRect(6.4, 6.4), "wood", 70, "mansion_velvet_particle"),
    furniture("globe", "Globe", new CircleHitbox(2.4), "wood", 60, "mansion_wood_particle", { residue: false, rotationMode: RotationMode.Full }),
    furniture("library_table", "Reading Table", RectangleHitbox.fromRect(14, 7), "wood", 120, "mansion_wood_particle", { hasLoot: true, lootTable: "desk" }),
    furniture("grand_dining_table", "Dining Table", RectangleHitbox.fromRect(32, 9), "wood", 200, "mansion_wood_particle"),
    furniture("dining_chair", "Dining Chair", RectangleHitbox.fromRect(5, 5), "wood", 50, "mansion_wood_particle", { residue: false }),
    furniture("sideboard", "Sideboard", RectangleHitbox.fromRect(14, 4.4), "wood", 100, "mansion_wood_particle", { hasLoot: true, lootTable: "large_drawer" }),
    furniture("china_cabinet", "China Cabinet", RectangleHitbox.fromRect(12, 4), "wood", 80, "mansion_wood_particle"),
    furniture("range_cooker", "Range Cooker", RectangleHitbox.fromRect(12, 5), "metal_light", 160, "mansion_white_particle", { reflectBullets: true }),
    furniture("mansion_counter", "Kitchen Counter", RectangleHitbox.fromRect(16, 4.6), "wood", 100, "mansion_white_particle", { hasLoot: true, lootTable: "small_drawer" }),
    furniture("mansion_sink_counter", "Kitchen Sink", RectangleHitbox.fromRect(16, 4.6), "wood", 100, "mansion_white_particle", { residue: "mansion_counter_residue" }),
    furniture("kitchen_island_marble", "Kitchen Island", RectangleHitbox.fromRect(18, 7), "wood", 160, "mansion_white_particle", { residue: "kitchen_island_residue" }),
    furniture("four_poster_bed", "Four-Poster Bed", RectangleHitbox.fromRect(16, 19), "wood", 160, "mansion_wood_particle"),
    furniture("chaise_longue", "Chaise Longue", RectangleHitbox.fromRect(6, 14), "wood", 80, "mansion_velvet_particle"),
    furniture("vanity_table", "Dressing Table", RectangleHitbox.fromRect(10, 4.6), "wood", 70, "mansion_white_particle", { residue: false, hasLoot: true, lootTable: "small_drawer" }),
    furniture("clawfoot_tub", "Clawfoot Bath", RectangleHitbox.fromRect(7.6, 14), "porcelain", 180, "mansion_white_particle"),
    furniture("double_vanity", "Double Basin", RectangleHitbox.fromRect(16, 5), "wood", 110, "mansion_white_particle", { hasLoot: true, lootTable: "mansion_cabinet" }),
    furniture("dressing_island", "Dressing Island", RectangleHitbox.fromRect(12, 7), "wood", 120, "mansion_walnut_particle", { hasLoot: true, lootTable: "small_drawer" }),
    furniture("executive_desk", "Partners' Desk", RectangleHitbox.fromRect(16, 7.6), "wood", 180, "mansion_wood_particle", { hasLoot: true, lootTable: "desk" }),
    furniture("mansion_safe", "Safe", RectangleHitbox.fromRect(5, 5), "iron", 300, "metal_particle", {
        hasLoot: true, reflectBullets: true, allowFlyover: FlyoverPref.Never
    }),
    furniture("drinks_trolley", "Drinks Trolley", RectangleHitbox.fromRect(5, 3.2), "metal_light", 50, "window_particle", { residue: false }),
    // ---- B, the new house: the great room, the cinema, the gym; upstairs the master's suite, the games room, the office
    furniture("sectional_sofa", "Corner Sofa", new GroupHitbox(
        RectangleHitbox.fromRect(24, 6.6, Vec(0, -4.7)),
        RectangleHitbox.fromRect(6.6, 16, Vec(-8.7, 0))
    ), "wood", 140, "mansion_linen_particle"),
    furniture("media_console", "Media Console", RectangleHitbox.fromRect(20, 3.6), "wood", 100, "mansion_walnut_particle", { hasLoot: true, lootTable: "small_drawer" }),
    furniture("coffee_table_modern", "Coffee Table", RectangleHitbox.fromRect(10, 6), "stone", 90, "mansion_white_particle"),
    fixture("linear_fireplace", "Fireplace", RectangleHitbox.fromRect(16, 2.6), { material: "metal_heavy", allowFlyover: FlyoverPref.Always }),
    furniture("dining_table_modern", "Dining Table", RectangleHitbox.fromRect(24, 9), "wood", 160, "mansion_oak_particle"),
    furniture("modern_chair", "Chair", RectangleHitbox.fromRect(4.6, 4.6), "wood", 40, "mansion_graphite_particle", { residue: false }),
    furniture("kitchen_island_quartz", "Kitchen Island", RectangleHitbox.fromRect(22, 7.6), "wood", 160, "mansion_white_particle"),
    furniture("modern_counter", "Kitchen Counter", RectangleHitbox.fromRect(16, 4.6), "wood", 100, "mansion_graphite_particle", { hasLoot: true, lootTable: "small_drawer" }),
    furniture("modern_sink_counter", "Kitchen Sink", RectangleHitbox.fromRect(16, 4.6), "wood", 100, "mansion_graphite_particle", { residue: "modern_counter_residue" }),
    furniture("cinema_recliners", "Recliners", RectangleHitbox.fromRect(21, 7.4), "wood", 160, "mansion_leather_particle"),
    onWall("cinema_screen", "Cinema Screen", 30, 1.2),
    furniture("popcorn_machine", "Popcorn Machine", RectangleHitbox.fromRect(3.4, 3.4), "appliance", 60, "mansion_red_particle", { residue: false }),
    furniture("treadmill", "Treadmill", RectangleHitbox.fromRect(5.4, 13), "appliance", 140, "mansion_graphite_particle"),
    furniture("billiards_table", "Billiards Table", RectangleHitbox.fromRect(17, 9.6), "wood", 200, "mansion_wood_particle"),
    furniture("arcade_cabinet", "Arcade Cabinet", RectangleHitbox.fromRect(4.4, 4.4), "appliance", 90, "mansion_graphite_particle", { variations: 2, allowFlyover: FlyoverPref.Never }),
    furniture("platform_bed", "Bed", RectangleHitbox.fromRect(17, 20), "wood", 160, "mansion_walnut_particle"),
    furniture("freestanding_tub", "Bath", RectangleHitbox.fromRect(7, 13), "porcelain", 180, "mansion_white_particle"),
    furniture("floating_vanity", "Double Basin", RectangleHitbox.fromRect(16, 4.6), "wood", 110, "mansion_white_particle", { hasLoot: true, lootTable: "mansion_cabinet" }),
    furniture("glass_desk", "Glass Desk", RectangleHitbox.fromRect(14, 6.4), "glass", 100, "window_particle", { hasLoot: true, lootTable: "desk" }),
    // B's games room's bar (the pool villa's too, until 1.07.1)
    piece("bar_counter", "Bar Counter", RectangleHitbox.fromRect(20, 5), {
        material: "wood", health: 180, hasLoot: true, lootTable: "mansion_bar", allowFlyover: FlyoverPref.Always,
        frames: { particle: "bar_counter_particle", residue: "bar_counter_residue" }
    }),
    // the garage's
    furniture("workbench", "Workbench", RectangleHitbox.fromRect(16, 5), "wood", 140, "mansion_oak_particle", { hasLoot: true, lootTable: "small_drawer" }),

    // ---------------- the town's street lights (0.98.1, tools/fargo/street_lights.py) ----------------
    // a steel post at a road's edge, its arm out over the road (the picture's +y, turned to the road), drawn over whoever
    // walks under it; lit at night while it stands (`lamp`), shot or punched down to its stump, dark (its glass heard)
    piece("street_light", "Street Light", new CircleHitbox(1), {
        material: "metal_heavy",
        health: 100,
        allowFlyover: FlyoverPref.Never,
        zIndex: ZIndexes.ObstaclesLayer3,
        additionalDestroySounds: ["glass_destroyed"],
        frames: { particle: "metal_particle", residue: "street_light_residue" },
        lamp: { offset: Vec(0, STREET_LIGHT.reach), radius: STREET_LIGHT.light }
    })
];

/** the pieces' tinted particles (as obstacles.ts's TintedParticles: the colours of their pictures) */
export const FARGO_PARTICLES: Readonly<Record<string, { readonly base: string, readonly tint: number }>> = {
    // the station's red brick
    police_brick_particle_1: { base: "stone_particle_1", tint: 0x8a5a44 },
    police_brick_particle_2: { base: "stone_particle_2", tint: 0x7a4c38 },
    police_desk_particle: { base: "wood_particle", tint: 0x6a5a48 },
    police_seat_particle: { base: "metal_particle_1", tint: 0x3a4a6a },
    police_copier_particle: { base: "metal_particle_1", tint: 0xb8b8b2 },
    police_shelf_particle: { base: "metal_particle_2", tint: 0x5a6670 },
    police_barrier_particle: { base: "wood_particle", tint: 0xd8d4c8 },
    traffic_cone_particle: { base: "plastic_particle", tint: 0xe86a1a },
    watch_desk_particle: { base: "metal_particle_1", tint: 0x5a6266 },
    // Pool Day's
    pool_lounger_particle: { base: "plastic_particle", tint: 0xf2f2ee },
    pool_float_particle: { base: "plastic_particle", tint: 0xf48aa8 },
    pool_blue_particle: { base: "metal_particle_1", tint: 0x3f6fba },
    pool_red_particle: { base: "metal_particle_1", tint: 0xb84a3e },
    pool_bench_particle: { base: "wood_particle", tint: 0xb98a52 },
    pool_white_particle: { base: "plastic_particle", tint: 0xf2f1ec },
    pool_kiosk_particle: { base: "wood_particle", tint: 0xf2c94c },
    beach_ball_particle: { base: "plastic_particle", tint: 0xe8402e },
    swim_ring_particle: { base: "plastic_particle", tint: 0xf26a3a },
    // the mansions'
    mansion_stone_particle_1: { base: "stone_particle_1", tint: 0xd8cfba },
    mansion_stone_particle_2: { base: "stone_particle_2", tint: 0xc4b898 },
    mansion_stone_particle: { base: "stone_particle_1", tint: 0xd8cfba },
    mansion_wood_particle: { base: "wood_particle", tint: 0x5e2f1a },
    mansion_walnut_particle: { base: "wood_particle", tint: 0x553823 },
    mansion_oak_particle: { base: "wood_particle", tint: 0xc49a66 },
    mansion_leather_particle: { base: "wood_particle", tint: 0x5a1a16 },
    mansion_velvet_particle: { base: "wood_particle", tint: 0x2e4a36 },
    mansion_linen_particle: { base: "wood_particle", tint: 0xa8aaa6 },
    mansion_white_particle: { base: "plastic_particle", tint: 0xf2f0ea },
    mansion_graphite_particle: { base: "metal_particle_1", tint: 0x3a3c40 },
    mansion_red_particle: { base: "plastic_particle", tint: 0xb8241e },
    bar_counter_particle: { base: "wood_particle", tint: 0x5a3a22 }
};
